import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTable, useSortBy, useFilters, useGlobalFilter, usePagination } from 'react-table'
import { useParams } from "react-router-dom";
import axios from "axios";
import styled from 'styled-components'

import Pagination from "./Pagination.js";
import StyledInput from "./StyledInput.js"

// Define a default UI for filtering
function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter },
}) {
    // const count = preFilteredRows.length
    const { t } = useTranslation();

    return (
        <StyledInput
            value={filterValue || ''}
            onChange={e => {
                setFilter(e.target.value || undefined) // Set undefined to remove the filter entirely
            }}
            // placeholder={`${count} records...`}
            placeholder={t('texts.list.search')}
        />
    )
}

function countX(preGlobalFilteredRows, field) {
    var count_Gesamt = { all: 0 }
    preGlobalFilteredRows.forEach(element => {
        if (count_Gesamt[element.original.Kontinent] === undefined)
            count_Gesamt[element.original.Kontinent] = 0;
        count_Gesamt[element.original.Kontinent] += parseInt(element.original[field])
        count_Gesamt['all'] += parseInt(element.original[field])
    });

    return count_Gesamt
}

function Table({ data }) {

    const { lang } = useParams();
    const { t, i18n } = useTranslation();

    // Change the language dynamically
    React.useEffect(() => {
        i18n.changeLanguage(lang);
    }, [lang, i18n]);

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
            // width: "auto",
        }),
        []
    )

    const columns = React.useMemo(() => [
        // {
        //     Header: t('texts.list.kontinent'),
        //     accessor: 'Kontinent',
        //     show: false,
        // },
        {
            Header: t('texts.list.land'),
            accessor: 'Land',
            width: 100,
            Filter: <></>,
        },
        {
            Header: t('texts.list.all'),
            accessor: 'Gesamt',
            width: 100,
            Filter: <></>,
        },
        {
            Header: t('texts.list.sum_standard'),
            accessor: 'Summe_von_StandardHL',
            width: 100,
            Filter: <></>,
        },
        {
            Header: t('texts.list.sum_top'),
            accessor: 'Summe_von_AnzahlHL',
            width: 100,
            Filter: <></>,
        },
        {
            Header: t('texts.list.sum_mega'),
            accessor: 'Summe_von_AnzahlMHL',
            width: 100,
            Filter: <></>,
        },
        {
            Header: t('texts.list.sum_wel_unique'),
            accessor: 'wel_unique',
            width: 100,
            Filter: <></>,
        },
        {
            Header: t('texts.list.sum_wel'),
            accessor: 'Sum_WE',
            width: 100,
            Filter: <></>,
        },
    ], [t]);

    const summaryLineTitle = React.useMemo(() => t('texts.sumlist.sumlabel'), [t]);

    const initialState = React.useMemo(() => ({ pageIndex: 0 }), []);

    var table = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            // filterTypes,
            initialState,
        },
        useFilters, // useFilters!
        useGlobalFilter, // useGlobalFilter!
        useSortBy,
        usePagination,
        // useBlockLayout
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page, // Instead of using 'rows', we'll use page,
        prepareRow,
        preGlobalFilteredRows
    } = table;

    // counts per Kontinent
    var fields = ['Gesamt', 'Summe_von_StandardHL', 'Summe_von_AnzahlHL', 'Summe_von_AnzahlMHL', 'wel_unique', 'Sum_WE'];
    var counts = {};
    fields.forEach(field => {
        counts[field] = countX(preGlobalFilteredRows, field)
    })

    return (
        <>
            <table {...getTableProps({ style: { width: "100%" } })} id="sumlist_table">
                <thead>
                    {headerGroups.map(headerGroup => {
                        const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                        return (
                            <tr key={key} {...headerGroupProps}>
                                {headerGroup.headers.map(column => {
                                    const { key, ...headerProps } = column.getHeaderProps();
                                    return (
                                        <th key={key} {...headerProps} className={'table-header-' + column.id}>
                                            <span className="title">{column.render('Header')}</span>
                                            {/* Add a sort direction indicator */}
                                            <span>
                                                {column.isSorted
                                                    ? column.isSortedDesc
                                                        ? ' 🔽'
                                                        : ' 🔼'
                                                    : ''}
                                            </span>
                                            {/* Render the columns filter UI */}
                                            <div>{column.canFilter ? column.render('Filter') : null}</div>
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    }
                    )}
                    <Pagination table={table} t={t} showTour={false} selectionText={null} />
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                        prepareRow(row)

                        const currentKontinent = row.original.Kontinent;
                        const previousKontinent = i > 0 ? page[i - 1].original.Kontinent : null;
                        const isNewKontinent = currentKontinent !== previousKontinent;

                        const originalIndex = preGlobalFilteredRows.findIndex(r => r.id === row.id);
                        const nextKontinent = originalIndex < preGlobalFilteredRows.length - 1 ? preGlobalFilteredRows[originalIndex + 1].original.Kontinent : null;
                        const isLastEntry = currentKontinent !== nextKontinent;

                        const endOfTable = originalIndex === preGlobalFilteredRows.length-1

                        const { key, ...rowProps } = row.getRowProps();
                        return (
                            <>
                                {isNewKontinent && <tr key={`kont-${currentKontinent}`}>
                                    <td colSpan={columns.length}><strong>{currentKontinent}</strong></td>
                                </tr>}
                                <tr key={key} {...rowProps}>
                                    {row.cells.map(cell => {
                                        const { key, ...cellProps } = cell.getCellProps();
                                        return <td key={key} {...cellProps}
                                            style={{
                                                backgroundColor: row.original.TopLevel ? 'yellow' : 'inherit',
                                            }}
                                        >{cell.render('Cell')}</td>
                                    })}
                                </tr>
                                {isLastEntry && <><tr key={`kont-${currentKontinent}`}>
                                    <td><strong>{currentKontinent}</strong></td>
                                    {fields.map(field => {
                                        return <td><strong>{counts[field][currentKontinent]}</strong></td>
                                    })}
                                </tr><tr><td colSpan={columns.length}></td></tr></>}
                                {endOfTable && <tr key="all">
                                    <td><strong>{summaryLineTitle}</strong></td>
                                    {fields.map(field => {
                                        return <td><strong>{counts[field]["all"]}</strong></td>
                                    })}
                                </tr>}
                            </>
                        )
                    })}
                </tbody>
                <tfoot>
                    {preGlobalFilteredRows.length > 0 && <Pagination table={table} t={t} showTour={false} selectionText={null} />}
                </tfoot>
            </table >
        </>
    )
}

const Styles = styled.div`
  padding: 1rem;

  table {
    border-spacing: 0;
    border: 1px solid black;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid black;
      border-right: 1px solid black;
      width: 100px;

      :last-child {
        border-right: 0;
      }
    }

    tbody > tr > td:not(:first-child) {
      text-align: center;
    }

    th {
      vertical-align: bottom;
    }

    th span {
      background-color: transparent;
      color: white;
    }

    th span.title {
      text-transform: uppercase;
      margin-bottom: 1rem;
      display: inline-block;
    }

    thead > tr:nth-child(1) > th {
      background-color: var(--fire);
    }

    thead > tr:nth-child(2) > th,
    tfoot > tr > th {
      background-color: black;
    }

    th > div,
    th > div > div {
      background-color: transparent
    }
  }
`

const SumList = () => {
    const [data, setData] = useState([]);

    const { lang } = useParams();
    const { t, i18n } = useTranslation();
    // Change the language dynamically
    React.useEffect(() => {
        i18n.changeLanguage(lang);

        var url = `${process.env.REACT_APP_DATA_HOST}/${lang}/sum_list`
        axios.get(url).then(res => {
            const data = res.data.data;
            // console.log(data);
            setData(data);
        })
    }, [lang, i18n]);

    return (
        <>
            <h2>{t('texts.sumlist.title')}</h2>
            <div>
                {t('texts.sumlist.text1')}
            </div>
            <Styles>
                <Table data={data} />
            </Styles>
        </>
    )
}

export default SumList;